// Simplified API service for frontend2
// Based on frontend/src/services/api/api.service.tsx

import AuthService from './auth.service';

export class ApiConnectionError extends Error {
  constructor(msg: string) {
    super(msg);
    Object.setPrototypeOf(this, ApiConnectionError.prototype);
  }
}

interface ErrorPayload {
  exception: string;
  msg: string;
}

export class ApiError extends Error {
  code: number;
  payload?: ErrorPayload;
  constructor(msg: string, code: number, payload?: ErrorPayload) {
    super(msg);
    this.code = code;
    this.payload = payload;
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

const serialize = function (obj: any) {
  const str: string[] = [];
  for (const p in obj) {
    if (obj.hasOwnProperty(p)) {
      str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]));
    }
  }
  return str.join('&');
};

export default class ApiService {
  private static instance: ApiService;

  hostname: string;
  basePath: string;

  public static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  constructor() {
    const authService = AuthService.getInstance();
    this.hostname = authService.apiUrl || `https://${window.location.hostname}`;
    this.basePath = '/api/v1';
  }

  public apiUrl() {
    return this.hostname + this.basePath;
  }

  private getAccessToken(): string | null {
    return AuthService.getInstance().getAccessToken();
  }

  /**
   * Determines the branch header value (RUTHIE, DRAFT, or PROD) based on:
   * - Page: If on Ruthie page (/ruthie/*), always RUTHIE
   * - User role: If not Builder or Admin, always PROD
   * - Draft mode: If Builder/Admin and in draft mode, DRAFT; otherwise PROD
   */
  private getBranchHeader(): string {

    // Check if we're on the Ruthie page - use RUTHIE branch
    if (window.location.pathname.startsWith('/ruthie')) {
      return 'RUTHIE';
    }

    const authState = AuthService.getInstance().getAuthState();

    if (!authState || !authState.roles || authState.roles.length === 0) {
      // No roles, default to PROD
      return 'PROD';
    }

    // Normalize role names for comparison
    const normalizedRoles = authState.roles.map((role: any) => {
      const roleString = typeof role === 'string'
        ? role
        : role?.name || role?.key || '';
      return roleString.toLowerCase();
    });

    // Check if user is Builder or Admin
    const isBuilderOrAdmin = normalizedRoles.some(role =>
      role === 'builder' || role === 'admin'
    );

    // If not Builder or Admin, hardcode to PROD
    if (!isBuilderOrAdmin) {
      return 'PROD';
    }

    // For Builder/Admin, check draft mode
    const draftVersionId = localStorage.getItem('draft_version_id');
    return draftVersionId ? 'DRAFT' : 'PROD';
  }

  public async request(
    method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
    path: string,
    payload?: object
  ): Promise<object> {
    let body = null;
    let query = '';

    if (method === 'POST' || method === 'PATCH' || method === 'PUT') {
      body = JSON.stringify(payload);
    }

    if (method === 'GET' && !!payload) {
      query = '?' + serialize(payload);
    }

    const token = this.getAccessToken();
    const branch = this.getBranchHeader();

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Plb-Branch': branch,
      };

      if (token) {
        headers['Authorization'] = 'Bearer ' + token;
      }

      const response = await fetch(this.apiUrl() + path + query, {
        method: method,
        headers,
        body: body,
        mode: 'cors',
        credentials: 'include',
      });

      if (response.status < 200 || response.status >= 400) {
        throw new ApiError('API Error', response.status, await response.json().catch(() => undefined));
      } else if (response.status === 204) {
        return {};
      }
      return await response.json();
    } catch (err) {
      if (err instanceof ApiError) {
        throw err;
      } else if (err instanceof Error && err.message == 'Failed to fetch') {
        console.error(err);
        throw new ApiConnectionError(err.message);
      } else if (err instanceof Error) {
        console.error(err);
        throw new ApiError(err.message, -1, undefined);
      } else {
        throw err;
      }
    }
  }

  public async *requestStream(method: string, path: string, body: any): AsyncGenerator<any> {
    const token = this.getAccessToken();
    const branch = this.getBranchHeader();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Connection': 'keep-alive',
      'Plb-Branch': branch,
    };

    if (token) {
      headers['Authorization'] = 'Bearer ' + token;
    }

    const response = await fetch(this.apiUrl() + path, {
      method: method,
      headers,
      body: JSON.stringify(body),
      mode: 'cors',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    if (!reader) {
      throw new Error('No response body');
    }

    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const decoded = decoder.decode(value, { stream: true });
        buffer += decoded;

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6);
            if (jsonStr.trim() && jsonStr !== '[DONE]') {
              try {
                const data = JSON.parse(jsonStr);
                yield data;
              } catch (e) {
                console.warn('Failed to parse JSON:', jsonStr);
              }
            }
          }
        }
      }

      if (buffer.trim() && buffer.startsWith('data: ')) {
        const jsonStr = buffer.slice(6);
        if (jsonStr.trim() && jsonStr !== '[DONE]') {
          try {
            const data = JSON.parse(jsonStr);
            yield data;
          } catch (e) {
            console.warn('Failed to parse final JSON:', jsonStr);
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }
}

